import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  CheckCircle2,
  ClipboardList,
  Clock,
  CircleDot,
  Columns2,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDown,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search,
  User as UserIcon,
  ChevronDown,
  ChevronUp,
  X,
  XCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  slate: '#0f172a'
};

const normalizeLower = (value) => String(value || '').trim().toLowerCase();

const translateRuolo = (value) => {
  const map = { commerciale: 'Commerciale', legale: 'Legale', pagamenti: 'Pagamenti', entrambi: 'Entrambi' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const translateStato = (value) => {
  const map = {
    aperta: 'Aperta',
    in_verifica: 'In verifica',
    confermata: 'Confermata',
    archiviata: 'Archiviata',
    respinta: 'Respinta'
  };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const translateGravita = (value) => {
  const map = {
    standard: 'Standard',
    grave: 'Grave',
    bloccante: 'Bloccante'
  };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const GravitaChip = ({ gravita }) => {
  const key = String(gravita || '').toLowerCase();
  const style =
    key === 'bloccante'
      ? { color: UI_COLORS.red }
      : key === 'grave'
      ? { color: UI_COLORS.amber }
      : key === 'standard'
      ? { color: UI_COLORS.indigo }
      : { color: 'inherit' };
  return (
    <Chip
      label={translateGravita(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize', fontWeight: 700, color: style.color, borderColor: style.color }}
    />
  );
};

const StatoChip = ({ stato }) => {
  const key = String(stato || '').toLowerCase();
  const style =
    key === 'confermata'
      ? { color: UI_COLORS.green }
      : key === 'respinta'
      ? { color: UI_COLORS.red }
      : key === 'in_verifica'
      ? { color: UI_COLORS.amber }
      : key === 'archiviata'
      ? { color: UI_COLORS.indigo }
      : { color: UI_COLORS.sky };
  return (
    <Chip
      label={translateStato(key) || '-'}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize', fontWeight: 700, color: style.color, borderColor: style.color }}
    />
  );
};

const STATUS_DEFS = [
  { key: 'aperta', label: 'Aperta', color: UI_COLORS.sky, icon: CircleDot },
  { key: 'in_verifica', label: 'In verifica', color: UI_COLORS.amber, icon: Clock },
  { key: 'confermata', label: 'Confermata', color: UI_COLORS.green, icon: CheckCircle2 },
  { key: 'archiviata', label: 'Archiviata', color: UI_COLORS.indigo, icon: Archive },
  { key: 'respinta', label: 'Respinta', color: UI_COLORS.red, icon: XCircle }
];

const StatoWidget = ({ label, value, subtitle, subtitleColor, color, icon: Icon, selected, onClick }) => (
  <Paper
    elevation={0}
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 2,
      minHeight: 84,
      cursor: 'pointer',
      userSelect: 'none',
      borderColor: selected ? color : 'divider',
      bgcolor: selected ? `${color}08` : 'background.paper',
      display: 'flex',
      alignItems: 'center'
    }}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.15, color: selected ? color : 'text.primary' }} noWrap>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" sx={{ mt: 0.25, fontWeight: 800, color: subtitleColor || 'text.secondary' }} noWrap>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  </Paper>
);

const formatDateIt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: formatDateIso(from), to: formatDateIso(to) };
};

const parseIsoDateOnly = (value) => {
  if (!value || typeof value !== 'string') return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const addDaysIso = (iso, days) => {
  const d = parseIsoDateOnly(iso);
  if (!d) return '';
  d.setDate(d.getDate() + Number(days || 0));
  return formatDateIso(d);
};

const diffDaysInclusive = (fromIso, toIso) => {
  const from = parseIsoDateOnly(fromIso);
  const to = parseIsoDateOnly(toIso);
  if (!from || !to) return 0;
  const ms = to.getTime() - from.getTime();
  const days = Math.floor(ms / 86400000);
  return days + 1;
};

const buildPrevRange = (fromIso, toIso) => {
  const len = diffDaysInclusive(fromIso, toIso);
  if (!len) return { prevFrom: '', prevTo: '' };
  const prevTo = addDaysIso(fromIso, -1);
  const prevFrom = addDaysIso(fromIso, -len);
  return { prevFrom, prevTo };
};

const formatNumberIt = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('it-IT').format(n);
};

const formatPercent1It = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
};

const buildVsPrevSubtitle = (deltaAbs, deltaPct) => {
  const dA = Number(deltaAbs || 0);
  const isNeg = dA < 0;
  const sign = dA > 0 ? '+' : '';
  const pct = deltaPct === null || deltaPct === undefined ? '—' : `${sign}${formatPercent1It(deltaPct)}`;
  return {
    text: `${sign}${formatNumberIt(dA)} (${pct}%) vs prec.`,
    color: isNeg ? UI_COLORS.red : UI_COLORS.green
  };
};

const parseBlobError = async (data) => {
  try {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed?.message || text;
      } catch (_) {
        return text;
      }
    }
    if (typeof data === 'object') return data?.message || null;
    return null;
  } catch (_) {
    return null;
  }
};

const normalizeCustomerLabel = (customer) => {
  if (!customer) return '';
  const label = String(customer.full_name || '').trim();
  if (label) return label;
  if (customer.id) return `Cliente #${customer.id}`;
  return '';
};

export default function BlacklistSegnalazioni() {
  const { user: authUser } = useAuth();
  const { addJob } = useJob();
  const isAdmin = normalizeLower(authUser?.role) === 'admin' || normalizeLower(authUser?.role_name) === 'admin';
  const currentUserId = authUser?.id ? String(authUser.id) : '';
  const currentUserOption = useMemo(() => {
    if (!authUser?.id) return null;
    return {
      id: authUser.id,
      name: authUser.name || authUser.email || `Utente #${authUser.id}`
    };
  }, [authUser]);

  const columnDefs = useMemo(() => {
    const cols = [
      { key: 'data_segnalazione', label: 'Data', sortable: true, align: 'left' },
      { key: 'ruolo_segnalato', label: 'Ruolo', sortable: true, align: 'left' },
      { key: 'stato', label: 'Stato', sortable: true, align: 'left' },
      { key: 'utente_segnalato_name', label: 'Segnalato', sortable: true, align: 'left' }
    ];
    
    if (isAdmin) {
      cols.push({ key: 'utente_segnalante_name', label: 'Segnalante', sortable: true, align: 'left' });
    }
    
    cols.push(
      { key: 'cliente_id', label: 'Cliente', sortable: true, align: 'left' },
      { key: 'stima_danno_economico', label: 'Stima €', sortable: true, align: 'right' },
      { key: 'note', label: 'Note', sortable: false, align: 'left' }
    );
    return cols;
  }, [isAdmin]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_segnalazione');
  const [filterText, setFilterText] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterRuolo, setFilterRuolo] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(() => getCurrentMonthRange().from);
  const [filterDateTo, setFilterDateTo] = useState(() => getCurrentMonthRange().to);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'data_segnalazione',
    'ruolo_segnalato',
    'stato',
    'utente_segnalato_name',
    ...(isAdmin ? ['utente_segnalante_name'] : []),
    'cliente_id',
    'stima_danno_economico'
  ]);

  const [customerById, setCustomerById] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [dialogTab, setDialogTab] = useState('segnalazione');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [detailsBySegnalazioneId, setDetailsBySegnalazioneId] = useState({});
  const [detailsLoadingBySegnalazioneId, setDetailsLoadingBySegnalazioneId] = useState({});
  const [accessDialogOpen, setAccessDialogOpen] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMode, setReportMode] = useState('riepilogo');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');

  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tipiOptions, setTipiOptions] = useState([]);
  const [loadingTipi, setLoadingTipi] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerInputValue, setCustomerInputValue] = useState('');

  const [formData, setFormData] = useState({
    utente_segnalante_id: '',
    utente_segnalato_id: '',
    ruolo_segnalato: 'commerciale',
    cliente_id: '',
    data_segnalazione: '',
    stato: 'aperta',
    note: '',
    stima_danno_economico: 0,
    dettaglio: []
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/blacklist/segnalazioni');
      const list = Array.isArray(response.data) ? response.data : [];
      setRows(list);
    } catch (err) {
      console.error('Error fetching blacklist segnalazioni:', err);
      toast.error('Errore nel caricamento delle segnalazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleAccessConfirm = () => {
    setAccessDialogOpen(false);
    setAccessGranted(true);
  };

  const handleAccessCancel = () => {
    setAccessDialogOpen(false);
    window.history.back();
  };

  const toggleRowInadempienze = async (row) => {
    const id = row?.id ? String(row.id) : '';
    if (!id) return;

    setExpandedRowIds((prev) => {
      const exists = prev.includes(id);
      if (exists) return [];
      return [id];
    });

    if (detailsBySegnalazioneId[id] || detailsLoadingBySegnalazioneId[id]) return;
    setDetailsLoadingBySegnalazioneId((p) => ({ ...p, [id]: true }));
    try {
      const res = await api.get(`/blacklist/segnalazioni/${id}`);
      const dettaglio = Array.isArray(res.data?.dettaglio) ? res.data.dettaglio : [];
      setDetailsBySegnalazioneId((p) => ({ ...p, [id]: dettaglio }));
    } catch (_) {
      setDetailsBySegnalazioneId((p) => ({ ...p, [id]: [] }));
    } finally {
      setDetailsLoadingBySegnalazioneId((p) => ({ ...p, [id]: false }));
    }
  };

  const fetchCustomersByIds = async (ids) => {
    const unique = Array.from(new Set((ids || []).filter(Boolean).map((v) => String(v))));
    const missing = unique.filter((id) => !customerById[id]).slice(0, 50);
    if (missing.length === 0) return;
    try {
      const results = await Promise.all(
        missing.map(async (id) => {
          try {
            const res = await api.get(`/gtl-pratiche/customers/by-id/${id}`);
            return res.data || null;
          } catch (_) {
            return null;
          }
        })
      );
      setCustomerById((prev) => {
        const next = { ...prev };
        results.forEach((c) => {
          if (c?.id != null) next[String(c.id)] = c;
        });
        return next;
      });
    } catch (_) {}
  };

  useEffect(() => {
    if (!accessGranted) return undefined;
    fetchRows();
    return undefined;
  }, [accessGranted]);

  useEffect(() => {
    const ids = rows.map((r) => r?.cliente_id).filter(Boolean);
    if (ids.length > 0) fetchCustomersByIds(ids);
  }, [rows]);

  useEffect(() => {
    if (!openDialog) {
      return undefined;
    }

    const term = String(customerInputValue || '').trim();
    const timeoutId = window.setTimeout(async () => {
      if (!term) {
        const currentId = String(formData.cliente_id || '').trim();
        const currentCustomer = currentId && customerById[currentId] ? [customerById[currentId]] : [];
        setCustomerOptions(currentCustomer);
        return;
      }

      setCustomerLoading(true);
      try {
        const response = await api.get('/gtl-pratiche/customers', {
          params: {
            search: term,
            limit: 20
          }
        });

        const options = Array.isArray(response.data) ? response.data : [];
        const currentId = String(formData.cliente_id || '').trim();
        const currentCustomer = currentId && customerById[currentId] ? [customerById[currentId]] : [];
        const merged = [...currentCustomer, ...options];
        const byId = new Map();
        merged.forEach((item) => {
          if (item?.id) {
            byId.set(String(item.id), item);
          }
        });
        const normalized = Array.from(byId.values());
        setCustomerOptions(normalized);
        setCustomerById((prev) => {
          const next = { ...prev };
          normalized.forEach((c) => {
            if (c?.id != null) next[String(c.id)] = c;
          });
          return next;
        });
      } catch (err) {
        console.error('Errore lookup clienti (segnalazioni):', err);
      } finally {
        setCustomerLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [customerInputValue, openDialog, formData.cliente_id, customerById]);

  const loadUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const res = await api.get('/users/lookup', { params: { limit: 500 } });
      const list = res.data?.users || [];
      setUserOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error fetching users for blacklist:', e);
      setUserOptions([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTipi = async () => {
    if (loadingTipi) return;
    setLoadingTipi(true);
    try {
      const res = await api.get('/blacklist/tipi-inadempienza');
      const list = Array.isArray(res.data) ? res.data : [];
      setTipiOptions(list);
    } catch (_) {
      setTipiOptions([]);
    } finally {
      setLoadingTipi(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const compareText = (a, b) => {
    const sa = String(a || '').toLowerCase();
    const sb = String(b || '').toLowerCase();
    if (sa < sb) return -1;
    if (sa > sb) return 1;
    return 0;
  };

  const filteredRows = rows
    .filter((row) => {
      if (filterStato && String(row?.stato || '') !== filterStato) return false;
      if (filterRuolo && String(row?.ruolo_segnalato || '') !== filterRuolo) return false;
      if (filterDateFrom && String(row?.data_segnalazione || '') < filterDateFrom) return false;
      if (filterDateTo && String(row?.data_segnalazione || '') > filterDateTo) return false;
      if (!filterText) return true;
      const s = filterText.toLowerCase();
      return (
        String(row?.utente_segnalato_name || '').toLowerCase().includes(s) ||
        String(row?.utente_segnalante_name || '').toLowerCase().includes(s) ||
        String(row?.note || '').toLowerCase().includes(s) ||
        String(row?.cliente_id || '').toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      const valA = a[orderBy];
      const valB = b[orderBy];
      if (orderBy === 'data_segnalazione') {
        const da = valA ? new Date(valA).getTime() : 0;
        const db = valB ? new Date(valB).getTime() : 0;
        return isAsc ? da - db : db - da;
      }
      const cmp = compareText(valA, valB);
      return isAsc ? cmp : -cmp;
    });

  const rowsForStatoWidgets = useMemo(() => {
    return rows.filter((row) => {
      if (filterRuolo && String(row?.ruolo_segnalato || '') !== filterRuolo) return false;
      if (filterDateFrom && String(row?.data_segnalazione || '') < filterDateFrom) return false;
      if (filterDateTo && String(row?.data_segnalazione || '') > filterDateTo) return false;
      if (!filterText) return true;
      const s = filterText.toLowerCase();
      return (
        String(row?.utente_segnalato_name || '').toLowerCase().includes(s) ||
        String(row?.utente_segnalante_name || '').toLowerCase().includes(s) ||
        String(row?.note || '').toLowerCase().includes(s) ||
        String(row?.cliente_id || '').toLowerCase().includes(s)
      );
    });
  }, [rows, filterRuolo, filterDateFrom, filterDateTo, filterText]);

  const prevRange = useMemo(() => {
    if (!filterDateFrom || !filterDateTo) return { prevFrom: '', prevTo: '' };
    return buildPrevRange(filterDateFrom, filterDateTo);
  }, [filterDateFrom, filterDateTo]);

  const rowsForStatoWidgetsPrev = useMemo(() => {
    const prevFrom = prevRange.prevFrom;
    const prevTo = prevRange.prevTo;
    if (!prevFrom || !prevTo) return [];
    return rows.filter((row) => {
      if (filterRuolo && String(row?.ruolo_segnalato || '') !== filterRuolo) return false;
      if (String(row?.data_segnalazione || '') < prevFrom) return false;
      if (String(row?.data_segnalazione || '') > prevTo) return false;
      if (!filterText) return true;
      const s = filterText.toLowerCase();
      return (
        String(row?.utente_segnalato_name || '').toLowerCase().includes(s) ||
        String(row?.utente_segnalante_name || '').toLowerCase().includes(s) ||
        String(row?.note || '').toLowerCase().includes(s) ||
        String(row?.cliente_id || '').toLowerCase().includes(s)
      );
    });
  }, [rows, filterRuolo, prevRange.prevFrom, prevRange.prevTo, filterText]);

  const statoCounts = useMemo(() => {
    const acc = STATUS_DEFS.reduce((m, s) => {
      m[s.key] = 0;
      return m;
    }, {});
    rowsForStatoWidgets.forEach((r) => {
      const k = String(r?.stato || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(acc, k)) acc[k] += 1;
    });
    return acc;
  }, [rowsForStatoWidgets]);

  const statoCountsPrev = useMemo(() => {
    const acc = STATUS_DEFS.reduce((m, s) => {
      m[s.key] = 0;
      return m;
    }, {});
    rowsForStatoWidgetsPrev.forEach((r) => {
      const k = String(r?.stato || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(acc, k)) acc[k] += 1;
    });
    return acc;
  }, [rowsForStatoWidgetsPrev]);

  const statoVsPrev = useMemo(() => {
    const out = {};
    STATUS_DEFS.forEach((s) => {
      const cur = Number(statoCounts[s.key] || 0);
      const prev = Number(statoCountsPrev[s.key] || 0);
      const deltaAbs = cur - prev;
      const deltaPct = prev > 0 ? (deltaAbs / prev) * 100 : null;
      out[s.key] = { cur, prev, deltaAbs, deltaPct };
    });
    return out;
  }, [statoCounts, statoCountsPrev]);

  const displayedColumns = columnDefs.filter((col) => visibleColumns.includes(col.key));

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(columnKey);
      if (isVisible && prev.length === 1) return prev;
      if (isVisible) return prev.filter((key) => key !== columnKey);
      return [...prev, columnKey];
    });
  };

  const openCreate = () => {
    setCurrentRow(null);
    setDialogTab('segnalazione');
    setFormData({
      utente_segnalante_id: isAdmin ? '' : currentUserId,
      utente_segnalato_id: '',
      ruolo_segnalato: 'commerciale',
      cliente_id: '',
      data_segnalazione: '',
      stato: 'aperta',
      note: '',
      stima_danno_economico: 0,
      dettaglio: []
    });
    setCustomerInputValue('');
    loadUsers();
    loadTipi();
    setOpenDialog(true);
  };

  const openEdit = async (row) => {
    setDialogTab('segnalazione');
    loadUsers();
    loadTipi();
    try {
      const res = await api.get(`/blacklist/segnalazioni/${row.id}`);
      const data = res.data || {};
      setCurrentRow(data);
      setFormData({
        utente_segnalante_id: isAdmin
          ? data.utente_segnalante_id
            ? String(data.utente_segnalante_id)
            : ''
          : currentUserId || (data.utente_segnalante_id ? String(data.utente_segnalante_id) : ''),
        utente_segnalato_id: data.utente_segnalato_id ? String(data.utente_segnalato_id) : '',
        ruolo_segnalato: data.ruolo_segnalato || 'commerciale',
        cliente_id: data.cliente_id ? String(data.cliente_id) : '',
        data_segnalazione: data.data_segnalazione ? String(data.data_segnalazione).slice(0, 10) : '',
        stato: data.stato || 'aperta',
        note: data.note || '',
        stima_danno_economico:
          data.stima_danno_economico !== null && data.stima_danno_economico !== undefined && data.stima_danno_economico !== ''
            ? Number(data.stima_danno_economico)
            : 0,
        dettaglio: Array.isArray(data.dettaglio)
          ? data.dettaglio.map((d) => ({
              tipo_inadempienza_id: d.tipo_inadempienza_id ? String(d.tipo_inadempienza_id) : '',
              riferimento_pratica: d.riferimento_pratica || '',
              note_specifiche: d.note_specifiche || ''
            }))
          : []
      });
      if (data.cliente_id) {
        const id = String(data.cliente_id);
        try {
          const customerRes = await api.get(`/gtl-pratiche/customers/by-id/${id}`);
          const customer = customerRes.data || null;
          if (customer?.id != null) {
            setCustomerById((prev) => ({ ...prev, [String(customer.id)]: customer }));
            setCustomerOptions((prev) => {
              const merged = [...prev, customer];
              const byId = new Map();
              merged.forEach((c) => {
                if (c?.id) byId.set(String(c.id), c);
              });
              return Array.from(byId.values());
            });
            setCustomerInputValue(customer.full_name || '');
          } else {
            setCustomerInputValue('');
          }
        } catch (_) {
          setCustomerInputValue('');
        }
      } else {
        setCustomerInputValue('');
      }
      setOpenDialog(true);
    } catch (e) {
      console.error('Error loading segnalazione detail:', e);
    }
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setCurrentRow(null);
  };

  const handleSubmit = async () => {
    const payload = {
      utente_segnalante_id: isAdmin
        ? formData.utente_segnalante_id
          ? Number(formData.utente_segnalante_id)
          : null
        : authUser?.id
        ? Number(authUser.id)
        : null,
      utente_segnalato_id: formData.utente_segnalato_id ? Number(formData.utente_segnalato_id) : null,
      ruolo_segnalato: String(formData.ruolo_segnalato || '').trim().toLowerCase(),
      cliente_id: formData.cliente_id ? Number(formData.cliente_id) : null,
      data_segnalazione: String(formData.data_segnalazione || '').trim(),
      stato: String(formData.stato || '').trim().toLowerCase(),
      note: String(formData.note || '').trim(),
      stima_danno_economico:
        formData.stima_danno_economico === '' || formData.stima_danno_economico === null || formData.stima_danno_economico === undefined
          ? 0
          : Number(formData.stima_danno_economico),
      dettaglio: Array.isArray(formData.dettaglio)
        ? formData.dettaglio
            .map((d) => ({
              tipo_inadempienza_id: d.tipo_inadempienza_id ? Number(d.tipo_inadempienza_id) : null,
              riferimento_pratica: String(d.riferimento_pratica || '').trim(),
              note_specifiche: String(d.note_specifiche || '').trim()
            }))
            .filter((d) => d.tipo_inadempienza_id)
        : []
    };

    if (!payload.utente_segnalato_id || !payload.data_segnalazione) {
      toast.error(isAdmin ? 'Seleziona segnalante, segnalato e data' : 'Seleziona segnalato e data');
      return;
    }

    try {
      if (currentRow?.id) {
        await api.put(`/blacklist/segnalazioni/${currentRow.id}`, payload);
        toast.success('Segnalazione aggiornata');
      } else {
        await api.post('/blacklist/segnalazioni', payload);
        toast.success('Segnalazione creata');
      }
      closeDialog();
      fetchRows();
    } catch (e) {
      console.error('Error saving segnalazione:', e);
      toast.error((await parseBlobError(e?.response?.data)) || e?.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/blacklist/segnalazioni/${deleteTarget.id}`);
      toast.success('Segnalazione eliminata');
      closeDeleteDialog();
      fetchRows();
    } catch (e) {
      console.error('Error deleting segnalazione:', e);
      toast.error(e?.response?.data?.message || 'Errore durante eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const pickUserOption = (id) => {
    const sid = String(id || '');
    const found = userOptions.find((u) => String(u.id) === sid) || null;
    if (found) return found;
    if (currentUserOption && String(currentUserOption.id) === sid) return currentUserOption;
    return null;
  };

  const pickTipoOption = (id) => {
    const sid = String(id || '');
    return tipiOptions.find((t) => String(t.id) === sid) || null;
  };

  const pickCustomerOption = (id) => {
    const sid = String(id || '');
    return customerOptions.find((c) => String(c.id) === sid) || null;
  };

  const addDettaglioRow = () => {
    setFormData((p) => ({
      ...p,
      dettaglio: [...(Array.isArray(p.dettaglio) ? p.dettaglio : []), { tipo_inadempienza_id: '', riferimento_pratica: '', note_specifiche: '' }]
    }));
  };

  const updateDettaglioRow = (idx, patch) => {
    setFormData((p) => {
      const list = Array.isArray(p.dettaglio) ? [...p.dettaglio] : [];
      if (!list[idx]) return p;
      list[idx] = { ...list[idx], ...patch };
      return { ...p, dettaglio: list };
    });
  };

  const removeDettaglioRow = (idx) => {
    setFormData((p) => {
      const list = Array.isArray(p.dettaglio) ? [...p.dettaglio] : [];
      list.splice(idx, 1);
      return { ...p, dettaglio: list };
    });
  };

  const handleStartReport = async () => {
    const from = String(reportDateFrom || '').trim();
    const to = String(reportDateTo || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      toast.error('Data da non valida');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(to)) {
      toast.error('Data a non valida');
      return;
    }
    if (from > to) {
      toast.error('Intervallo non valido: la data da è successiva alla data a');
      return;
    }

    try {
      setReportLoading(true);
      const res = await api.post('/reports/blacklist-segnalazioni', {
        data_da: from,
        data_a: to,
        stato: filterStato || '',
        ruolo: filterRuolo || '',
        search: filterText || '',
        include_details: reportMode === 'dettaglio'
      });
      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error('Job ID non ricevuto');
      }
      addJob(jobId, 'blacklist_segnalazioni_report');
      setReportDialogOpen(false);
      toast.success('Generazione report segnalazioni avviata');
    } catch (error) {
      console.error('Errore avvio report segnalazioni:', error);
      toast.error(error.response?.data?.message || error.message || 'Errore avvio report segnalazioni');
    } finally {
      setReportLoading(false);
    }
  };

  const openReportDialog = () => {
    setReportDateFrom(String(filterDateFrom || '').trim());
    setReportDateTo(String(filterDateTo || '').trim());
    setReportDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Dialog
        open={accessDialogOpen}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleAccessCancel();
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 'bold' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: '#fee2e2',
              color: '#b91c1c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AlertTriangle size={18} />
          </Box>
          Conferma gestione segnalazioni
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {`Stai per accedere alla gestione delle segnalazioni Blacklist. Questa sezione può contenere informazioni sensibili (nominativi, note interne, stato e storico richiami).\n\nAssicurati di avere un motivo legittimo e l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={handleAccessCancel}>
            Annulla
          </Button>
          <Button onClick={handleAccessConfirm}>Conferma</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: `${UI_COLORS.indigo}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ClipboardList size={20} color={UI_COLORS.indigo} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Segnalazioni
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione segnalazioni blacklist
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outline" onClick={openReportDialog} title="Report PDF">
            <div className="flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              <span>Report</span>
            </div>
          </Button>
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openCreate}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuova segnalazione
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))'
              },
              gap: 2,
              mb: 2.5,
              alignItems: 'stretch'
            }}
          >
            {STATUS_DEFS.map((s) => {
              const info = statoVsPrev?.[s.key] || { cur: 0, prev: 0, deltaAbs: 0, deltaPct: null };
              const canCompare = Boolean(prevRange?.prevFrom && prevRange?.prevTo);
              const subtitleObj = canCompare ? buildVsPrevSubtitle(info.deltaAbs || 0, info.deltaPct ?? null) : null;

              return (
                <StatoWidget
                  key={s.key}
                  label={s.label}
                  value={statoCounts[s.key] || 0}
                  subtitle={subtitleObj?.text || ''}
                  subtitleColor={subtitleObj?.color || 'text.secondary'}
                  color={s.color}
                  icon={s.icon}
                  selected={filterStato === s.key}
                  onClick={() => {
                    setFilterStato((prev) => (prev === s.key ? '' : s.key));
                    setPage(0);
                  }}
                />
              );
            })}
          </Box>

          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 340, flex: '0 0 auto' }}>
              <TextField
                placeholder="Cerca per nominativi, note, cliente..."
                variant="outlined"
                size="small"
                fullWidth
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato</InputLabel>
                <Select
                  value={filterStato}
                  label="Stato"
                  onChange={(e) => {
                    setFilterStato(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="aperta">Aperta</MenuItem>
                  <MenuItem value="in_verifica">In verifica</MenuItem>
                  <MenuItem value="confermata">Confermata</MenuItem>
                  <MenuItem value="archiviata">Archiviata</MenuItem>
                  <MenuItem value="respinta">Respinta</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sx={{ minWidth: 170, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Ruolo</InputLabel>
                <Select
                  value={filterRuolo}
                  label="Ruolo"
                  onChange={(e) => {
                    setFilterRuolo(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="commerciale">Commerciale</MenuItem>
                  <MenuItem value="legale">Legale</MenuItem>
                  <MenuItem value="pagamenti">Pagamenti</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sx={{ minWidth: 300, flex: '0 0 auto', display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Da"
                InputLabelProps={{ shrink: true }}
                value={filterDateFrom}
                onChange={(e) => {
                  setFilterDateFrom(e.target.value);
                  setPage(0);
                }}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label="A"
                InputLabelProps={{ shrink: true }}
                value={filterDateTo}
                onChange={(e) => {
                  setFilterDateTo(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>

            <Grid item sx={{ minWidth: 180, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outline"
                onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                title="Colonne"
              >
                <Columns2 className="h-4 w-4" />
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
              >
                {columnDefs.map((col) => (
                  <MenuItem key={col.key} onClick={() => handleToggleColumn(col.key)}>
                    <Checkbox checked={visibleColumns.includes(col.key)} />
                    {col.label}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {displayedColumns.map((col) => (
                    <TableCell key={col.key} align={col.align}>
                      {col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleRequestSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 120 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                      Nessun record trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const sid = row?.id ? String(row.id) : '';
                      const expanded = Boolean(sid && expandedRowIds.includes(sid));
                      const detailLoading = Boolean(sid && detailsLoadingBySegnalazioneId[sid]);
                      const detailRows = sid ? detailsBySegnalazioneId[sid] : null;
                      const detailCount = Array.isArray(detailRows) ? detailRows.length : null;

                      return (
                        <>
                          <TableRow key={row.id} hover>
                            {displayedColumns.map((col) => {
                              if (col.key === 'data_segnalazione') {
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                    {formatDateIt(row.data_segnalazione) || 'n/d'}
                                  </TableCell>
                                );
                              }
                              if (col.key === 'ruolo_segnalato') {
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                    {translateRuolo(row.ruolo_segnalato)}
                                  </TableCell>
                                );
                              }
                              if (col.key === 'stato') {
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                    <StatoChip stato={row.stato} />
                                  </TableCell>
                                );
                              }
                              if (col.key === 'utente_segnalato_name') {
                                const count = Number(row?.inadempienze_count || 0);
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ minWidth: 240 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 1,
                                          minWidth: 0
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 0 }} noWrap>
                                          {row.utente_segnalato_name || ''}
                                        </Typography>
                                        <Tooltip title={expanded ? 'Nascondi inadempienze' : 'Mostra inadempienze'}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleRowInadempienze(row);
                                            }}
                                            sx={{ ml: 0.5 }}
                                          >
                                            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      <Tooltip title="Numero di inadempienze inserite nella segnalazione (righe nel tab “Inadempienze”).">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                          <ClipboardList size={14} color={UI_COLORS.amber} />
                                          <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 800, color: 'text.secondary' }}
                                            noWrap
                                          >
                                            {Number.isFinite(count) ? count : 0}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                );
                              }
                              if (col.key === 'cliente_id') {
                                const id = row?.cliente_id ? String(row.cliente_id) : '';
                                const customer = id ? customerById[id] : null;
                                const label = id ? normalizeCustomerLabel(customer) || `#${id}` : '-';
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                    {id ? (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                                          {label}
                                        </Typography>
                                        {customer?.email ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <MailIcon size={14} />
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                              {customer.email}
                                            </Typography>
                                          </Box>
                                        ) : null}
                                        {customer?.telephone ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <PhoneIcon size={14} />
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                              {customer.telephone}
                                            </Typography>
                                          </Box>
                                        ) : null}
                                      </Box>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                );
                              }
                              if (col.key === 'stima_danno_economico') {
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                      €{' '}
                                      {Number(row?.stima_danno_economico || 0).toLocaleString('it-IT', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })}
                                    </Typography>
                                  </TableCell>
                                );
                              }
                              if (col.key === 'note') {
                                return (
                                  <TableCell key={col.key} align={col.align} sx={{ maxWidth: 380 }}>
                                    <Typography variant="body2" className="truncate">
                                      {row.note || ''}
                                    </Typography>
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                  {row[col.key] ?? ''}
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => openEdit(row)}>
                                <EditIcon className="h-4 w-4 text-slate-900 dark:text-slate-100" />
                              </IconButton>
                              <IconButton size="small" onClick={() => openDeleteDialog(row)}>
                                <DeleteIcon className="h-4 w-4 text-red-500" />
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          {expanded ? (
                            <TableRow key={`${sid}-details`}>
                              <TableCell colSpan={displayedColumns.length + 1}>
                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                  {detailLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                      <CircularProgress size={24} />
                                    </Box>
                                  ) : Array.isArray(detailRows) && detailRows.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                      Nessuna inadempienza trovata per questa segnalazione.
                                    </Typography>
                                  ) : (
                                    <Box sx={{ py: 1 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Inadempienze{detailCount != null ? ` (${detailCount})` : ''}
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Gravità</TableCell>
                                            <TableCell>Inadempienza</TableCell>
                                            <TableCell>Rif.</TableCell>
                                            <TableCell>Note</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {(detailRows || []).map((d) => (
                                            <TableRow
                                              key={d.id || `${d.tipo_inadempienza_id}-${d.created_at || ''}`}
                                              hover
                                            >
                                              <TableCell sx={{ width: 120 }}>
                                                <GravitaChip gravita={d.tipo_gravita} />
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 700 }}>
                                                {d.tipo_descrizione || '-'}
                                              </TableCell>
                                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {d.riferimento_pratica || '-'}
                                              </TableCell>
                                              <TableCell sx={{ maxWidth: 520 }}>
                                                <Typography variant="body2" color="text.secondary" className="truncate">
                                                  {d.note_specifiche || '-'}
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{currentRow?.id ? 'Modifica segnalazione' : 'Nuova segnalazione'}</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={dialogTab}
            onChange={(_e, value) => setDialogTab(value)}
            sx={{ mb: 2, minHeight: 40 }}
          >
            <Tab value="segnalazione" label="Segnalazione" sx={{ minHeight: 40 }} />
            <Tab
              value="inadempienze"
              label={`Inadempienze (${Array.isArray(formData.dettaglio) ? formData.dettaglio.length : 0})`}
              sx={{ minHeight: 40 }}
            />
          </Tabs>

          {dialogTab === 'segnalazione' ? (
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  size="small"
                  options={userOptions}
                  loading={loadingUsers}
                  value={pickUserOption(formData.utente_segnalante_id)}
                  disabled={!isAdmin}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  getOptionLabel={(o) => (o?.name ? String(o.name) : o?.email ? String(o.email) : '')}
                  onChange={(_e, v) => setFormData((p) => ({ ...p, utente_segnalante_id: v?.id ? String(v.id) : '' }))}
                  renderInput={(params) => <TextField {...params} label="Segnalante" placeholder="Seleziona utente" />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  size="small"
                  options={userOptions}
                  loading={loadingUsers}
                  value={pickUserOption(formData.utente_segnalato_id)}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  getOptionLabel={(o) => (o?.name ? String(o.name) : '')}
                  onChange={(_e, v) => setFormData((p) => ({ ...p, utente_segnalato_id: v?.id ? String(v.id) : '' }))}
                  renderInput={(params) => <TextField {...params} label="Segnalato" placeholder="Seleziona utente" />}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ruolo segnalato</InputLabel>
                  <Select
                    value={formData.ruolo_segnalato}
                    label="Ruolo segnalato"
                    onChange={(e) => setFormData((p) => ({ ...p, ruolo_segnalato: e.target.value }))}
                  >
                    <MenuItem value="commerciale">Commerciale</MenuItem>
                    <MenuItem value="legale">Legale</MenuItem>
                    <MenuItem value="pagamenti">Pagamenti</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={formData.stato}
                    label="Stato"
                    onChange={(e) => setFormData((p) => ({ ...p, stato: e.target.value }))}
                  >
                    <MenuItem value="aperta">Aperta</MenuItem>
                    <MenuItem value="in_verifica">In verifica</MenuItem>
                    <MenuItem value="confermata">Confermata</MenuItem>
                    <MenuItem value="archiviata">Archiviata</MenuItem>
                    <MenuItem value="respinta">Respinta</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label="Data segnalazione"
                  InputLabelProps={{ shrink: true }}
                  value={formData.data_segnalazione}
                  onChange={(e) => setFormData((p) => ({ ...p, data_segnalazione: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  label="Stima danno economico"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={String(formData.stima_danno_economico ?? '')}
                  onChange={(e) => setFormData((p) => ({ ...p, stima_danno_economico: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={customerOptions}
                  value={pickCustomerOption(formData.cliente_id)}
                  loading={customerLoading}
                  onChange={(_e, v) => {
                    const id = v?.id ? String(v.id) : '';
                    setFormData((p) => ({ ...p, cliente_id: id }));
                    if (v?.id != null) {
                      setCustomerById((prev) => ({ ...prev, [String(v.id)]: v }));
                      setCustomerOptions((prev) => {
                        const merged = [...prev, v];
                        const byId = new Map();
                        merged.forEach((c) => {
                          if (c?.id) byId.set(String(c.id), c);
                        });
                        return Array.from(byId.values());
                      });
                      setCustomerInputValue(v.full_name || '');
                    } else {
                      setCustomerInputValue('');
                    }
                  }}
                  onInputChange={(_event, value) => setCustomerInputValue(value)}
                  inputValue={customerInputValue}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  getOptionLabel={(option) => normalizeCustomerLabel(option)}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: '#eef2ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: 'primary.main',
                          mt: 0.25
                        }}
                      >
                        <UserIcon size={18} />
                      </Box>
                      <Box sx={{ ml: 1.5, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {normalizeCustomerLabel(option)}
                        </Typography>
                        {option?.telephone ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                            <PhoneIcon size={14} />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {option.telephone}
                            </Typography>
                          </Box>
                        ) : null}
                        {option?.email ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                            <MailIcon size={14} />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {option.email}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente (opzionale)"
                      placeholder="Cerca per cognome, nome, telefono o email"
                      helperText={
                        pickCustomerOption(formData.cliente_id) ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {pickCustomerOption(formData.cliente_id)?.telephone ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <PhoneIcon size={13} />
                                <Typography variant="caption" color="text.secondary">
                                  {pickCustomerOption(formData.cliente_id).telephone}
                                </Typography>
                              </Box>
                            ) : null}
                            {pickCustomerOption(formData.cliente_id)?.email ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <MailIcon size={13} />
                                <Typography variant="caption" color="text.secondary">
                                  {pickCustomerOption(formData.cliente_id).email}
                                </Typography>
                              </Box>
                            ) : null}
                            {!pickCustomerOption(formData.cliente_id)?.telephone &&
                            !pickCustomerOption(formData.cliente_id)?.email ? (
                              <Typography variant="caption" color="text.secondary">
                                Cliente selezionato
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          'Lookup remoto sulla tabella customer'
                        )
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Note"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={formData.note}
                  onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))}
                  placeholder="Note generali"
                />
              </Grid>
            </Grid>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Dettaglio inadempienze
                </Typography>
                <Button variant="outline" onClick={addDettaglioRow}>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Riga
                </Button>
              </Box>

              {Array.isArray(formData.dettaglio) && formData.dettaglio.length > 0 ? (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {formData.dettaglio.map((row, idx) => (
                    <Box
                      key={`det-${idx}`}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'nowrap'
                      }}
                    >
                      <Autocomplete
                        size="small"
                        options={tipiOptions}
                        loading={loadingTipi}
                        value={pickTipoOption(row.tipo_inadempienza_id)}
                        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                        getOptionLabel={(o) => (o?.descrizione ? String(o.descrizione) : '')}
                        onChange={(_e, v) =>
                          updateDettaglioRow(idx, { tipo_inadempienza_id: v?.id ? String(v.id) : '' })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tipo inadempienza"
                            placeholder="Seleziona tipo"
                            sx={{ minWidth: 420 }}
                          />
                        )}
                      />
                      <TextField
                        size="small"
                        label="Rif. pratica"
                        value={row.riferimento_pratica}
                        onChange={(e) => updateDettaglioRow(idx, { riferimento_pratica: e.target.value })}
                        sx={{ width: 180 }}
                      />
                      <TextField
                        size="small"
                        label="Note specifiche"
                        value={row.note_specifiche}
                        onChange={(e) => updateDettaglioRow(idx, { note_specifiche: e.target.value })}
                        sx={{ flex: 1, minWidth: 260 }}
                      />
                      <IconButton size="small" onClick={() => removeDettaglioRow(idx)} title="Rimuovi riga">
                        <X className="h-4 w-4 text-red-500" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Nessuna riga inserita
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Elimina segnalazione</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: `${UI_COLORS.red}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AlertCircle size={18} color={UI_COLORS.red} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>{deleteTarget?.id ? `#${deleteTarget.id}` : 'n/d'}</Typography>
                <Typography variant="body2" color="text.secondary" className="truncate">
                  {deleteTarget?.utente_segnalato_name ? `Segnalato: ${deleteTarget.utente_segnalato_name}` : ''}
                </Typography>
              </Box>
            </Box>
            <Paper variant="outlined" elevation={0} sx={{ p: 1.5, borderRadius: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  gap: 0.75,
                  alignItems: 'center'
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Data
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {formatDateIt(deleteTarget?.data_segnalazione) || 'n/d'}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Stato
                </Typography>
                <Box>{deleteTarget?.stato ? <StatoChip stato={deleteTarget.stato} /> : '-'}</Box>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Ruolo
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {translateRuolo(deleteTarget?.ruolo_segnalato) || '-'}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Segnalante
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {deleteTarget?.utente_segnalante_name || '-'}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Cliente
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {(() => {
                    const id = deleteTarget?.cliente_id ? String(deleteTarget.cliente_id) : '';
                    const customer = id ? customerById?.[id] : null;
                    const label = id ? normalizeCustomerLabel(customer) || `#${id}` : '-';
                    return label;
                  })()}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Stima €
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                  €{' '}
                  {Number(deleteTarget?.stima_danno_economico || 0).toLocaleString('it-IT', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>

                {deleteTarget?.note ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      Note
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }} className="truncate">
                      {deleteTarget.note}
                    </Typography>
                  </>
                ) : null}
              </Box>
            </Paper>
            <Typography variant="body2" color="text.secondary">
              Questa operazione non è reversibile.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportDialogOpen} onClose={() => (reportLoading ? null : setReportDialogOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>Report PDF Segnalazioni</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
            Periodo di riferimento
          </Typography>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 220, flex: '0 0 auto' }}>
              <TextField
                label="Da"
                type="date"
                size="small"
                fullWidth
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item sx={{ minWidth: 220, flex: '0 0 auto' }}>
              <TextField
                label="A"
                type="date"
                size="small"
                fullWidth
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Riepilogo
            </FormLabel>
            <RadioGroup
              value={reportMode}
              onChange={(e) => setReportMode(e.target.value)}
              sx={{ gap: 0.5 }}
            >
              <FormControlLabel value="riepilogo" control={<Radio />} label="Solo riepilogo (tabella)" />
              <FormControlLabel value="dettaglio" control={<Radio />} label="Con dettaglio (inadempienze per segnalazione)" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Annulla
          </Button>
          <Button onClick={handleStartReport} disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
